import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckOutlined, LoadingOutlined } from "@ant-design/icons";
import CopyChatMsg from "../../../../assets/icons/CopyChatMsg";
import DownloadChatMsg from "../../../../assets/icons/DownloadChatMsg";
import ChevronIcon from "assets/icons/ChevronIcon";
import { copyToClipboard } from "helpers/utility/utilities";
import { downloadFile } from "helpers/utility/downloadFile";
import styles from "./CollapsibleSection.module.scss";
import { useScrollDetection } from "../../../../hooks/useScrollDetection";
import ChatTextAnimation from "../../VLChat/ChatTextAnimation";

interface CollapsibleSectionProps {
  title: string;
  content: string;
  isLoading?: boolean;
}

export default function CollapsibleSection({
  title,
  content,
  isLoading = false,
}: CollapsibleSectionProps) {
  // Start collapsed by default, auto-expand only when loading
  const [isExpanded, setIsExpanded] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  // Scroll detection hook
  const { scrolled, scrollElementRef: contentTextRef } =
    useScrollDetection<HTMLPreElement>();

  // Auto-expand when loading starts, close when loading finishes
  useEffect(() => {
    if (isLoading) {
      setIsExpanded(true);
    } else {
      setIsExpanded(false);
    }
  }, [isLoading]);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await copyToClipboard(content);
      setIsCopied(true);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleAnimationEnd = () => {
    setIsCopied(false); // Reset to copy icon when animation completes
  };

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();

    // Create a safe filename from the title
    const safeTitle = title.replace(/[^a-zA-Z0-9]/g, "_").toLowerCase();
    const suggestedName = `${safeTitle}.json`;
    const fileName = window.prompt("Enter file name", suggestedName);

    if (!fileName) return;

    // Use utility for clean download handling
    downloadFile(fileName, {
      type: "data",
      data: new Blob([content], { type: "application/json" }),
    });
  };

  return (
    <div className={`${styles.wrapper} ${isLoading ? styles.loading : ""}`}>
      <div
        className={`${styles.header} ${isLoading ? styles.headerLoading : ""}`}
        onClick={() => setIsExpanded(!isExpanded)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            setIsExpanded(!isExpanded);
          }
        }}
      >
        {isLoading ? (
          <ChatTextAnimation className={styles.title}>
            {`${title}...`}
          </ChatTextAnimation>
        ) : (
          <span className={styles.title}>{`${title}:`}</span>
        )}
        <div className={styles.headerIcons}>
          {isLoading && <LoadingOutlined spin />}
          {!isLoading && (
            <>
              <button
                onClick={handleDownload}
                title="Download"
                type="button"
                className={styles.headerButton}
              >
                <DownloadChatMsg />
              </button>
              <button
                onClick={handleCopy}
                title={isCopied ? "Copied!" : "Copy"}
                type="button"
                className={styles.headerButton}
              >
                {isCopied ? (
                  <CheckOutlined
                    className={styles.copied}
                    onAnimationEnd={handleAnimationEnd}
                  />
                ) : (
                  <CopyChatMsg />
                )}
              </button>
            </>
          )}
          {!isLoading && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
              title={isExpanded ? "Collapse" : "Expand"}
              type="button"
              className={styles.headerButton}
            >
              <ChevronIcon direction={isExpanded ? "up" : "down"} />
            </button>
          )}
        </div>
      </div>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            className={`${styles.content} ${isLoading ? styles.loadingContent : ""} ${scrolled ? styles.scrolled : ""}`}
            onClick={() => setIsExpanded(!isExpanded)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                setIsExpanded(!isExpanded);
              }
            }}
            initial={{
              height: isLoading ? "150px" : 0,
              opacity: 0,
            }}
            animate={{
              height: isLoading ? "150px" : "auto",
              opacity: 1,
            }}
            exit={{ height: 0, opacity: 0 }}
            transition={{
              height: {
                duration: isLoading ? 0 : 0.6,
                ease: "easeInOut",
              },
              opacity: { duration: 0.8, ease: "easeOut" },
            }}
          >
            <pre ref={contentTextRef} className={styles.contentText}>
              {content}
            </pre>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
